import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Clock, AlertCircle, Lock, ArrowRight } from 'lucide-react';

// Migration data - Updated by agent after each migration session
const MIGRATION_DATA = {
  lastUpdated: '2026-01-28',
  summary: {
    totalModules: 39,
    completed: 41,
    pending: 1,
    keepInServer: 2,
    notStarted: 0,
    serverPyLines: {
      start: 22000,
      current: 12476,
    },
    totalEndpointsMigrated: 182,
  },
  modules: [
    // ========== COMPLETED (Phase 6 - Jan 28, 2026: Suppliers, Clients, Complaints & Projects Migration) ==========
    { name: 'Suppliers', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Auto supplier_code, case-insensitive dupe check, PO sync, soft delete' },
    { name: 'Clients', status: 'COMPLETED', endpoints: 7, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Unique 5-letter code (3 name + 2 city), contact objects, SO sync, soft delete' },
    { name: 'Complaints', status: 'COMPLETED', endpoints: 11, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Linked delegation tasks for CA/PA, auto-complete tasks, cost analysis' },
    { name: 'Projects', status: 'COMPLETED', endpoints: 10, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Auto project/task codes, dependencies, progress tracking, milestones, super_admin delete' },
    // ========== COMPLETED (Phase 5 - Jan 28, 2026: User/Employee/Designation Unification) ==========
    { name: 'Users', status: 'COMPLETED', endpoints: 7, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Permissions on User doc, not Designation' },
    { name: 'Designations', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Decorative only - no permission control' },
    { name: 'HR Employees', status: 'COMPLETED', endpoints: 11, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Single source of truth for employee data' },
    { name: 'Employees (Legacy)', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Wrapper for hr_employees - backward compatible' },
    { name: 'Locations', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Merged best features from server.py' },
    { name: 'Holidays', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Identical code, simple deprecation' },
    { name: 'Notifications', status: 'COMPLETED', endpoints: 4, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Fixed is_read field discrepancy' },
    { name: 'Unit Addresses', status: 'COMPLETED', endpoints: 1, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Simple endpoint' },
    { name: 'Departments', status: 'COMPLETED', endpoints: 3, serverPyEndpoints: 0, testReport: 'Verified', notes: 'DB populated with Enum values' },
    // ========== COMPLETED (Phase 4 - Jan 26-27: Major Module Migrations) ==========
    { name: 'Sales Orders', status: 'COMPLETED', endpoints: 19, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'Pagination, search, filters added' },
    { name: 'Work Orders', status: 'COMPLETED', endpoints: 38, serverPyEndpoints: 0, testReport: 'iteration_34', notes: 'Full CRUD + jobs + receive' },
    { name: 'BOM', status: 'COMPLETED', endpoints: 15, serverPyEndpoints: 0, testReport: 'iteration_34', notes: 'Bulk upload + sync prices' },
    { name: 'FMS (Partial)', status: 'COMPLETED', endpoints: 15, serverPyEndpoints: 0, testReport: 'iteration_34', notes: 'All endpoints migrated including PATCH steps - TAT/branching supported' },
    { name: 'Production Plans', status: 'COMPLETED', endpoints: 11, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'hr_employees fallback added' },
    { name: 'Processes', status: 'COMPLETED', endpoints: 9, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'sync_process_to_transactions + BOM check' },
    { name: 'Variant Groups', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'BOM usage check added' },
    { name: 'Purchase Orders', status: 'COMPLETED', endpoints: 9, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'Auto-status calc + role-based access' },
    { name: 'Inventory (Simple)', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'GET list, movements, adjust' },
    { name: 'Inter-Unit Transfers', status: 'COMPLETED', endpoints: 7, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'Stock validation added' },
    { name: 'Fulfillment Tasks', status: 'COMPLETED', endpoints: 7, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'task_type + assigned_to_team filters' },
    { name: 'Pooled Jobs', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'Weight-based cost attribution' },
    { name: 'PO Discrepancies', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'iteration_36', notes: 'po_id filter + role-based approve' },
    { name: 'Indents', status: 'COMPLETED', endpoints: 12, serverPyEndpoints: 0, testReport: 'N/A', notes: 'All endpoints marked SUPERSEDED' },
    // ========== COMPLETED (Phase 1-3 - Jan 21-25) ==========
    { name: 'Checklists', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Gate Entries', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Customers', status: 'COMPLETED', endpoints: 2, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Dispatches', status: 'COMPLETED', endpoints: 3, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Auth', status: 'COMPLETED', endpoints: 4, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Admin', status: 'COMPLETED', endpoints: 3, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Migrated in Phase 1-3' },
    { name: 'Store', status: 'COMPLETED', endpoints: 28, serverPyEndpoints: 0, testReport: 'Verified', notes: '~41 endpoints migrated in Phase 1-3' },
    { name: 'Items', status: 'COMPLETED', endpoints: 11, serverPyEndpoints: 0, testReport: 'Verified', notes: 'nett_available + so_blocked calculations' },
    { name: 'Reports', status: 'COMPLETED', endpoints: 15, serverPyEndpoints: 0, testReport: 'Verified', notes: '12 endpoints migrated' },
    { name: 'Delegation Tasks', status: 'COMPLETED', endpoints: 9, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Email integration' },
    { name: 'OJC', status: 'COMPLETED', endpoints: 16, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Router registered and working' },
    { name: 'Pending BOM', status: 'COMPLETED', endpoints: 5, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Router registered and working' },
    { name: 'Variants Pending', status: 'COMPLETED', endpoints: 6, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Router registered and working' },
    { name: 'Announcements', status: 'COMPLETED', endpoints: 8, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Router registered and working' },
    { name: 'Cost Corrections', status: 'COMPLETED', endpoints: 3, serverPyEndpoints: 0, testReport: 'Verified', notes: 'Router registered and working' },
    // ========== PENDING ==========
    { name: 'MIS', status: 'PENDING', endpoints: 5, serverPyEndpoints: 5, testReport: 'Not tested', notes: 'Dashboard critical - careful review' },
    // ========== KEEP IN SERVER (Complex FIFO logic) ==========
    { name: 'Inventory Batches', status: 'KEEP_IN_SERVER', endpoints: 6, serverPyEndpoints: 4, testReport: 'N/A', notes: 'Complex FIFO logic - migrate when needed' },
    { name: 'Inventory Report', status: 'KEEP_IN_SERVER', endpoints: 0, serverPyEndpoints: 1, testReport: 'N/A', notes: '273 lines FIFO calculations' },
  ],
};

const getStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'KEEP_IN_SERVER':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'NOT_STARTED':
      return 'bg-gray-100 text-gray-600 border-gray-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'PENDING':
      return <Clock className="h-4 w-4 text-yellow-600" />;
    case 'KEEP_IN_SERVER':
      return <Lock className="h-4 w-4 text-blue-600" />;
    case 'NOT_STARTED':
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
    default:
      return null;
  }
};

const MigrationReportPage = () => {
  const { summary, modules, lastUpdated } = MIGRATION_DATA;
  const reductionPercent = ((summary.serverPyLines.start - summary.serverPyLines.current) / summary.serverPyLines.start * 100).toFixed(1);
  
  const completedModules = modules.filter(m => m.status === 'COMPLETED');
  const pendingModules = modules.filter(m => m.status === 'PENDING');
  const keepModules = modules.filter(m => m.status === 'KEEP_IN_SERVER');
  const notStartedModules = modules.filter(m => m.status === 'NOT_STARTED');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Server.py Migration Status</h1>
          <p className="text-slate-400">Last updated: {lastUpdated}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Server.py Reduction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{reductionPercent}%</div>
              <div className="text-sm text-slate-400 mt-1">
                {summary.serverPyLines.start.toLocaleString()} → {summary.serverPyLines.current.toLocaleString()} lines
              </div>
              <Progress value={parseFloat(reductionPercent)} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Modules Migrated</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{completedModules.length}</div>
              <div className="text-sm text-slate-400 mt-1">
                of {summary.totalModules} total modules
              </div>
              <Progress value={(completedModules.length / summary.totalModules) * 100} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Endpoints Migrated</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">{summary.totalEndpointsMigrated}</div>
              <div className="text-sm text-slate-400 mt-1">
                API endpoints now in /routes/
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400">Pending Migration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-400">{pendingModules.length}</div>
              <div className="text-sm text-slate-400 mt-1">
                modules awaiting migration
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Legend */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-sm text-slate-300">Completed ({completedModules.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-slate-300">Pending ({pendingModules.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-slate-300">Keep in Server ({keepModules.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-slate-300">Not Started ({notStartedModules.length})</span>
          </div>
        </div>

        {/* Completed Modules */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-green-400 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Completed Migrations ({completedModules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="pb-2 pr-4">Module</th>
                    <th className="pb-2 pr-4 text-center">Routes Endpoints</th>
                    <th className="pb-2 pr-4 text-center">Server.py</th>
                    <th className="pb-2 pr-4">Test Report</th>
                    <th className="pb-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {completedModules.map((module, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 pr-4 font-medium text-white">{module.name}</td>
                      <td className="py-2 pr-4 text-center text-green-400">{module.endpoints}</td>
                      <td className="py-2 pr-4 text-center text-slate-500">{module.serverPyEndpoints}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="text-xs bg-slate-700 text-slate-300 border-slate-600">
                          {module.testReport}
                        </Badge>
                      </td>
                      <td className="py-2 text-slate-400 text-xs">{module.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pending Modules */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-yellow-400 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Pending Migration ({pendingModules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="pb-2 pr-4">Module</th>
                    <th className="pb-2 pr-4 text-center">Routes Endpoints</th>
                    <th className="pb-2 pr-4 text-center">Server.py Active</th>
                    <th className="pb-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingModules.map((module, idx) => (
                    <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 pr-4 font-medium text-white flex items-center gap-2">
                        {module.name}
                        {module.notes.includes('Simple') && (
                          <Badge className="text-xs bg-green-900/50 text-green-400 border-green-700">Easy</Badge>
                        )}
                        {module.notes.includes('critical') && (
                          <Badge className="text-xs bg-red-900/50 text-red-400 border-red-700">Critical</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-center text-blue-400">{module.endpoints}</td>
                      <td className="py-2 pr-4 text-center text-yellow-400">{module.serverPyEndpoints}</td>
                      <td className="py-2 text-slate-400 text-xs">{module.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Keep in Server & Not Started */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-blue-400 flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Keep in Server.py ({keepModules.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {keepModules.map((module, idx) => (
                  <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
                    <div className="font-medium text-white">{module.name}</div>
                    <div className="text-xs text-slate-400 mt-1">{module.notes}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-gray-400 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Not Started ({notStartedModules.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notStartedModules.map((module, idx) => (
                  <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
                    <div className="font-medium text-white">{module.name}</div>
                    <div className="text-xs text-slate-400 mt-1">{module.notes}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>Migration workflow: Analyze → Get User Approval → Implement → Test → Deprecate</p>
          <p className="mt-1">Report auto-updated after each migration session</p>
        </div>
      </div>
    </div>
  );
};

export default MigrationReportPage;
